import '../styles/index.scss'

import React from 'react'
import get from 'lodash/get'
import NextApp from 'next/app'
import getConfig from 'next/config'
import Error from 'next/error'
import NextHead from 'next/head'
import querystring from 'query-string'

import { AuthProvider, Routes, Types as AuthTypes } from '@grandvision/auth'
import { withRedux } from '@grandvision/next'
import {
  GoogleMapsProvider,
  GoogleMapsSettings,
  Labels,
  LabelsObject,
  MediaQuery,
  Pin,
  ToastContainer,
} from '@grandvision/common'
import { DeliveryIndicatorProvider, StockIndicatorProvider } from '@grandvision/inventory'
import {
  ApiConfigProvider,
  Countries,
  Currencies,
  DistanceUnit,
  FeatureToggleProvider,
  IntlProvider,
  IntlSettings,
  Languages,
  mergeLabels,
  ReduxProvider,
  StoreWithPersistor,
} from '@grandvision/state'
import {
  BloomreachApi,
  BloomreachCmsProvider,
  BloomreachCmsSettings,
  BloomreachService,
  getClientSession,
  getServerSession,
  RuntimeContext,
  Session,
} from '@grandvision/bloomreach'
import { withSentry } from '@grandvision/analytics'
import { LocaleUtils } from '@grandvision/utils'

import { isAABEnabled, isMyAccountEnabled } from '../util/feature-toggle'

export interface NextProcess extends NodeJS.Process {
  browser: boolean
}

const { publicRuntimeConfig } = getConfig()

const cmsService = BloomreachService()

// My Account Routes
const routes: Routes = {
  createAccount: 'create',
  login: 'login',
  logout: 'logout',
  orders: 'orders',
  signup: 'signup',
  subscriptions: 'subscriptions',
  prescriptions: 'prescriptions',
  privacy: 'privacy',
  profile: 'profile',
  addressBook: 'address-book',
  resetPassword: 'reset-password',
  linkExpired: 'link-expired',
  setPassword: 'set-password',
  unlinkAppointment: 'unlink-appointment',
  unlinkOrder: 'unlink-order',
  changePassword: 'change-password',
}

const {
  grandvision: {
    apiUrl,
    features,
    myAccount: {
      auth0Domain,
      auth0ClientId,
      auth0CustomDomain,
      putPrescriptionTokensOnCart,
      disableAddressEditing = false as boolean,
    },
  },
} = publicRuntimeConfig

const authSettings = {
  apiUrl,
  basePath: 'my-account',
  clientId: auth0ClientId,
  customDomain: auth0CustomDomain,
  domain: auth0Domain,
  routes,
  checkoutRoute: '/checkout',
  putPrescriptionTokensOnCart,
  isAabEnabled: isAABEnabled(),
  disableAddressEditing,
}

const apiConfig = { apiUrl }

const googleMapsSettings: GoogleMapsSettings = {
  apiKey: publicRuntimeConfig.googleMaps.apiKey,
  defaultCenter: publicRuntimeConfig.googleMaps.mapCenter,
  defaultZoom: 6,
  pin: Pin({
    themes: {
      primary: { foreground: '#ffffff', background: '#302d2b' },
      highlight: { foreground: '#ffffff', background: '#ed2d25' },
    },
  }),
}

type Props = {
  pageProps: any
  store: StoreWithPersistor
  labels: BloomreachApi.LabelResources
  errorFlag: boolean
  initialBreakpoint: MediaQuery.BreakpointName
  bloomreachCmsSettings: BloomreachCmsSettings
  locale: string
}

class App extends NextApp<Props> {
  static async getInitialProps({ Component, ctx }) {
    const nextProcess = process as NextProcess

    if (ctx && ctx.res && ctx.res.statusCode === 500) {
      return {
        errorFlag: true,
      } as any
    }

    const session: Session =
      nextProcess && nextProcess.browser ? getClientSession({}) : getServerSession(ctx.req)

    cmsService.setCmsUrls(publicRuntimeConfig.bloomreach.cmsUrls)

    const preview = cmsService.isCmsPreview(session.search)
    const defaultLocale = get(publicRuntimeConfig, 'defaultLocale', 'en-IE')
    const locale = ctx.req ? LocaleUtils.getLocale(ctx.req, defaultLocale, preview) : defaultLocale

    // the "*" fetches all available label resources
    const tasks = [
      cmsService.fetchCmsResource(['*'], locale, publicRuntimeConfig.bloomreach.cmsUrls),
    ]

    const query = (ctx || {}).query || {}
    const hasPageModel = !!query.pageModel
    const request = {
      hostname: session.host,
      path: session.path || '/' + (session.search && `?${querystring.stringify(session.search)}`),
    }

    let errorFlag = false
    let data = ctx

    if (!hasPageModel) {
      try {
        const pageModel = await cmsService.fetchCmsPage(request, locale, session.cookie)
        const pageMeta = cmsService.getPageMeta(pageModel || {})

        data = {
          ...ctx,
          query: {
            search: query,
            request,
            preview,
            pageModel,
            locale,
            ...pageMeta,
          },
        }
      } catch (err) {
        console.error(err)
        errorFlag = true
      }
    }

    if (Component.getInitialProps && typeof Component.getInitialProps === 'function') {
      tasks.push(Component.getInitialProps.call(null, data))
    }

    const [resourceResult, pageProps] = await Promise.all(tasks)

    return {
      bloomreachCmsSettings: query.bloomreachCmsSettings,
      pageProps: pageProps || {},
      labels: resourceResult || {},
      errorFlag,
      initialBreakpoint: ctx.req && ctx.req.mediaQueryBreakpoint,
      locale,
    }
  }

  render() {
    const {
      Component,
      pageProps,
      store,
      labels,
      errorFlag,
      initialBreakpoint,
      bloomreachCmsSettings,
      locale,
    } = this.props

    if (errorFlag) {
      return <Error statusCode={500} />
    }

    const optIns: AuthTypes.OptInItem[] = [
      {
        title: Labels.getLabel(labels.account || {}, 'optIn.directMail'),
        tooltip: Labels.getLabel(labels.account || {}, 'optIn.directMail.tooltip', {
          hideLabelKey: true,
        }),
        value: 'marketingPost',
        isInSignup: true,
      },
      {
        title: Labels.getLabel(labels.account || {}, 'optIn.marketingEmails'),
        tooltip: Labels.getLabel(labels.account || {}, 'optIn.marketingEmails.tooltip', {
          hideLabelKey: true,
        }),
        value: 'marketingEmails',
        isInSignup: true,
      },
      {
        title: Labels.getLabel(labels.account || {}, 'optIn.generalCommunication'),
        tooltip: Labels.getLabel(labels.account || {}, 'optIn.generalCommunication.tooltip', {
          hideLabelKey: true,
        }),
        value: 'generalCommunication',
      },
    ]

    // @ts-ignore partial record not assignable to record
    const mergedLabels = mergeLabels(labels || {})

    const [language, country] = locale.split('-')
    const currency: Currencies = get(publicRuntimeConfig, 'currency', Currencies.GBP)
    const distanceUnit = DistanceUnit.mile
    const intlSettings: IntlSettings = {
      language: Languages[language],
      country: Countries[country],
      currency,
      distanceUnit,
    }

    // omit `bloomreach` from `publicRuntimeConfig`
    const {
      bloomreach,
      stockIndicator,
      deliveryIndicator,
      ...cleanPublicRuntimeConfig
    } = publicRuntimeConfig

    return (
      <React.StrictMode>
        <BloomreachCmsProvider settings={bloomreachCmsSettings}>
          <ReduxProvider store={store}>
            <FeatureToggleProvider enabledFeatures={features}>
              <IntlProvider settings={intlSettings} labels={mergedLabels}>
                <ApiConfigProvider apiConfig={apiConfig}>
                  <GoogleMapsProvider settings={googleMapsSettings}>
                    <StockIndicatorProvider settings={stockIndicator}>
                      <DeliveryIndicatorProvider settings={deliveryIndicator}>
                        <MediaQuery.Provider initialBreakpoint={initialBreakpoint}>
                          <AuthProvider
                            enabled={isMyAccountEnabled()}
                            settings={{ ...authSettings, optIns }}
                          >
                            <RuntimeContext.Provider
                              value={{ ...cleanPublicRuntimeConfig, headComponent: NextHead }}
                            >
                              <ToastContainer labels={labels as LabelsObject} />
                              <Component {...pageProps} labels={labels} store={store} />
                            </RuntimeContext.Provider>
                          </AuthProvider>
                        </MediaQuery.Provider>
                      </DeliveryIndicatorProvider>
                    </StockIndicatorProvider>
                  </GoogleMapsProvider>
                </ApiConfigProvider>
              </IntlProvider>
            </FeatureToggleProvider>
          </ReduxProvider>
        </BloomreachCmsProvider>
      </React.StrictMode>
    )
  }
}

export default withSentry(withRedux(App))
